// Define operation types
export enum ViewRenderStatus {
  SUCCESS = "SUCCESS",
  LOADING = "LOADING",
  ERROR = "ERROR",
  INIT = "INIT",
}
// Define operation types
export enum OperationType {
  CREATE = "CREATE",
  EDIT = "EDIT",
  VIEW = "VIEW",
  RESET = "RESET",
}

// Define action types
export enum ActionType {
  SET_VIEW_RENDER_STATUS = "SET_VIEW_RENDER_STATUS",
  SET_ERROR_MESSAGE = "SET_ERROR_MESSAGE",
  SET_SUCCESS_MESSAGE = "SET_SUCCESS_MESSAGE",
  SET_DATA = "SET_DATA",
  SET_OPERATION_TYPE = "SET_OPERATION_TYPE",
  SET_INPUT_CHANGE = "SET_INPUT_CHANGE",
  SET_DATA_CHANGE = "SET_DATA_CHANGE",
  FORCE_UPDATE = "FORCE_UPDATE",
}

export enum Themes {
  DARK = "DARK",
  LIGHT = "LIGHT",
  SYSTEM = "SYSTEM",
}

export const MODES = [
  {
    label: "Dark",
    value: Themes.DARK,
  },
  {
    label: "Light",
    value: Themes.LIGHT,
  },
  {
    label: "Auto",
    value: Themes.SYSTEM,
  },
];

export enum FieldType {
  TEXT = "text",
  TEXTAREA = "textarea",
  DROPDOWN = "dropdown",
  MULTISELECT = "multiselect",
  CHECKBOX = "checkbox",
}

export enum ConnectionStatus {
  CONNECTED = "connected",
  FETCHING = "fetching",
  DISCONNECTED = "disconnected",
}

//Filter chain categories
export enum FilterChainCategory {
  PROMPT_ENGINEERING = "Prompt Engineering",
  QUERY_GENERATION = "Generating Queries",
  SCHEMA_GENERATION = "Generating Schema",
  QUERY_EXECUTION = "Executing Queries",
  VISUALIZATION = "Generating Visualizations",
  UNCATEGORIZED = "Miscellaneous Tasks",
}

export enum PROJECT_STATE {
  UNSPECIFIED = "PROJECT_STATE_UNSPECIFIED",
  LOADING = "PROJECT_STATE_LOADING",
  ERROR = "PROJECT_STATE_ERROR",
  WARNING = "PROJECT_STATE_WARNING",
  READY = "PROJECT_STATE_READY",
  EMPTY = "PROJECT_STATE_EMPTY",
}

export enum PROJECT_URL_PARAM {
  PROJECT_STATE = "project_state",
  PROJECT_UUID = "project_uuid",
}

export enum PROJECT_CREATE_STATE {
  UNSPECIFIED = "unspecified",
  SETUP_PROJECT = "setup_project",
  SELECT_CONNECTIONS = "select_connections",
  SUCCESS = "success",
}

export enum PROJECT_EDIT_STATE {
  UNSPECIFIED = "unspecified",
  EDIT_PROJECT = "edit_project",
  EDIT_CONNECTIONS = "edit_connections",
  SUCCESS = "success",
}

// Define process prompt actions
export enum ProcessPromptAction {
  ACTION_UNSPECIFIED = "ACTION_UNSPECIFIED",
  ACTION_NEXT = "ACTION_NEXT",
  ACTION_VARIANT = "ACTION_VARIANT",
  ACTION_NEW_INVESTIGATION = "ACTION_NEW_INVESTIGATION",
  ACTION_RERUN_INVESTIGATION = "ACTION_RERUN_INVESTIGATION",
}

// Define process prompt status
export enum ProcessPromptStatus {
  STATUS_UNSPECIFIED = "STATUS_UNSPECIFIED",
  STATUS_IN_PROGRESS = "STATUS_IN_PROGRESS",
  STATUS_DONE = "STATUS_DONE",
  STATUS_ERROR = "STATUS_ERROR",
}

// Define message content types
export enum MessageContentType {
  CONTENT_TYPE_UNSPECIFIED = "CONTENT_TYPE_UNSPECIFIED",
  CONTENT_TYPE_DONE_INDICATOR = "CONTENT_TYPE_DONE_INDICATOR",
  CONTENT_TYPE_PROGRESS_STATUS = "CONTENT_TYPE_PROGRESS_STATUS",
  CONTENT_TYPE_CHAT_PROMPT = "CONTENT_TYPE_CHAT_PROMPT",
  CONTENT_TYPE_CHAT_RESPONSE = "CONTENT_TYPE_CHAT_RESPONSE",
  CONTENT_TYPE_FOLLOW_UP_SUGGESTIONS = "CONTENT_TYPE_FOLLOW_UP_SUGGESTIONS",
  CONTENT_TYPE_VISUALIZATION = "CONTENT_TYPE_VISUALIZATION",
  CONTENT_TYPE_SESSION_NAME = "CONTENT_TYPE_SESSION_NAME",
  CONTENT_TYPE_SOURCES = "CONTENT_TYPE_SOURCES",
  CONTENT_TYPE_CHAIN_OF_THOUGHT = "CONTENT_TYPE_CHAIN_OF_THOUGHT",
  CONTENT_TYPE_ALTERNATE_QUESTIONS = "CONTENT_TYPE_ALTERNATE_QUESTIONS",
  CONTENT_TYPE_ERROR_MESSAGE = "CONTENT_TYPE_ERROR_MESSAGE",
  CONTENT_TYPE_EXECUTION_TIME = "CONTENT_TYPE_EXECUTION_TIME",
  CONTENT_TYPE_MESSAGE = "CONTENT_TYPE_MESSAGE",
}

//Define rating constants
export enum Rating {
  RATING_UNSPECIFIED = "RATING_UNSPECIFIED",
  RATING_THUMBS_DOWN = "RATING_THUMBS_DOWN",
  RATING_THUMBS_UP = "RATING_THUMBS_UP",
}

export enum MessageSourceCategory {
  SOURCE_CATEGORY_UNSPECIFIED = "SOURCE_CATEGORY_UNSPECIFIED",
  SOURCE_CATEGORY_GENERIC = "SOURCE_CATEGORY_GENERIC",
  SOURCE_CATEGORY_EVENT = "SOURCE_CATEGORY_EVENT",
  SOURCE_CATEGORY_TRACE = "SOURCE_CATEGORY_TRACE",
  SOURCE_CATEGORY_CONFIG = "SOURCE_CATEGORY_CONFIG",
  SOURCE_CATEGORY_ALARM = "SOURCE_CATEGORY_ALARM",
  SOURCE_CATEGORY_LOG = "SOURCE_CATEGORY_LOG",
  SOURCE_CATEGORY_METRIC = "SOURCE_CATEGORY_METRIC",
}

export enum TelemetryType {
  UNSPECIFIED = "TELEMETRY_TYPE_UNSPECIFIED",
  ALERT = "TELEMETRY_TYPE_ALERT",
  CONFIG = "TELEMETRY_TYPE_CONFIG",
  LOG = "TELEMETRY_TYPE_LOG",
  METRIC = "TELEMETRY_TYPE_METRIC",
  TRACE = "TELEMETRY_TYPE_TRACE",
}

export enum TelemetryToProjectState {
  PROJECT_STATE_UNSPECIFIED = 0,
  PROJECT_STATE_LOADING = 1,
  PROJECT_STATE_ERROR = 2,
  PROJECT_STATE_WARNING = 3,
  PROJECT_STATE_READY = 4,
  PROJECT_STATE_EMPTY = 5,
}

export const TELEMETRY_TO_PROJECT_STATE_MAP: Record<number, PROJECT_STATE> = {
  [TelemetryToProjectState.PROJECT_STATE_UNSPECIFIED]: PROJECT_STATE.UNSPECIFIED,
  [TelemetryToProjectState.PROJECT_STATE_LOADING]: PROJECT_STATE.LOADING,
  [TelemetryToProjectState.PROJECT_STATE_ERROR]: PROJECT_STATE.ERROR,
  [TelemetryToProjectState.PROJECT_STATE_WARNING]: PROJECT_STATE.WARNING,
  [TelemetryToProjectState.PROJECT_STATE_READY]: PROJECT_STATE.READY,
  [TelemetryToProjectState.PROJECT_STATE_EMPTY]: PROJECT_STATE.EMPTY,
};

export enum TelemetryIconState {
  "LOADING" = "LOADING",
  "READY" = "READY",
  "ERROR" = "ERROR",
}

export const SOURCE_CATEGORY_LIST: {
  label: string;
  value: MessageSourceCategory;
}[] = [
  {
    label: "Metric",
    value: MessageSourceCategory.SOURCE_CATEGORY_METRIC,
  },
  {
    label: "Log",
    value: MessageSourceCategory.SOURCE_CATEGORY_LOG,
  },
  {
    label: "Config",
    value: MessageSourceCategory.SOURCE_CATEGORY_CONFIG,
  },
  {
    label: "Event",
    value: MessageSourceCategory.SOURCE_CATEGORY_EVENT,
  },
  {
    label: "Trace",
    value: MessageSourceCategory.SOURCE_CATEGORY_TRACE,
  },
  {
    label: "Alarm",
    value: MessageSourceCategory.SOURCE_CATEGORY_ALARM,
  },
  {
    label: "Generic",
    value: MessageSourceCategory.SOURCE_CATEGORY_GENERIC,
  },
];

// Label to value

export enum CoTStatus {
  IN_PROGRESS = "in_progress",
  DONE = "done",
  ERROR = "error",
  POSSIBLE_QUESTION = "possible_question",
}

export enum IncidentStatus {
  INCIDENT_STATUS_UNSPECIFIED = "INCIDENT_STATUS_UNSPECIFIED",
  INCIDENT_STATUS_INVESTIGATED = "INCIDENT_STATUS_INVESTIGATED",
  INCIDENT_STATUS_UNINVESTIGATED = "INCIDENT_STATUS_UNINVESTIGATED",
  INCIDENT_STATUS_OPEN = "INCIDENT_STATUS_OPEN",
  INCIDENT_STATUS_RESOLVED = "INCIDENT_STATUS_RESOLVED",
}

export enum IncidentType {
  INCIDENT_TYPE_UNSPECIFIED = "INCIDENT_TYPE_UNSPECIFIED",
  INCIDENT_TYPE_PAGERDUTY = "INCIDENT_TYPE_PAGERDUTY",
  INCIDENT_TYPE_DATADOG_MONITOR = "INCIDENT_TYPE_DATADOG_MONITOR",
  INCIDENT_TYPE_AWS_ALARM = "INCIDENT_TYPE_AWS_ALARM",
  INCIDENT_TYPE_AZURE_ALERT = "INCIDENT_TYPE_AZURE_ALERT",
  INCIDENT_TYPE_PROMETHEUS_ALERT = "INCIDENT_TYPE_PROMETHEUS_ALERT",
  INCIDENT_TYPE_ELASTIC_ALERT = "INCIDENT_TYPE_ELASTIC_ALERT",
  INCIDENT_TYPE_FIREHYDRANT = "INCIDENT_TYPE_FIREHYDRANT",
  INCIDENT_TYPE_INCIDENTIO = "INCIDENT_TYPE_INCIDENTIO",
  INCIDENT_TYPE_SERVICENOW = "INCIDENT_TYPE_SERVICENOW",
}

export const INCIDENT_TYPE_TO_NAME_MAP: Record<IncidentType, string> = {
  [IncidentType.INCIDENT_TYPE_PAGERDUTY]: "PagerDuty",
  [IncidentType.INCIDENT_TYPE_DATADOG_MONITOR]: "Datadog Monitor",
  [IncidentType.INCIDENT_TYPE_AWS_ALARM]: "AWS Alarm",
  [IncidentType.INCIDENT_TYPE_AZURE_ALERT]: "Azure Alert",
  [IncidentType.INCIDENT_TYPE_PROMETHEUS_ALERT]: "Prometheus Alert",
  [IncidentType.INCIDENT_TYPE_ELASTIC_ALERT]: "Elastic Alert",
  [IncidentType.INCIDENT_TYPE_FIREHYDRANT]: "FireHydrant",
  [IncidentType.INCIDENT_TYPE_INCIDENTIO]: "incident.io",
  [IncidentType.INCIDENT_TYPE_SERVICENOW]: "ServiceNow",
  [IncidentType.INCIDENT_TYPE_UNSPECIFIED]: "Unspecified",
};

export const INCIDENT_STATUS_TO_NAME_MAP: Record<IncidentStatus, string> = {
  [IncidentStatus.INCIDENT_STATUS_UNSPECIFIED]: "Unspecified",
  [IncidentStatus.INCIDENT_STATUS_OPEN]: "Open",
  [IncidentStatus.INCIDENT_STATUS_RESOLVED]: "Resolved",
  [IncidentStatus.INCIDENT_STATUS_INVESTIGATED]: "Investigated",
  [IncidentStatus.INCIDENT_STATUS_UNINVESTIGATED]: "Uninvestigated",
};

export enum InvestigationType {
  PENDING_INVESTIGATION = "PENDING_INVESTIGATION",
  INVESTIGATION_COMPLETED = "INVESTIGATION_COMPLETED",
}

export enum InvestigationStatus {
  INVESTIGATION_STATUS_UNSPECIFIED = "INVESTIGATION_STATUS_UNSPECIFIED",
  INVESTIGATION_STATUS_NOT_STARTED = "INVESTIGATION_STATUS_NOT_STARTED",
  INVESTIGATION_STATUS_IN_PROGRESS = "INVESTIGATION_STATUS_IN_PROGRESS",
  INVESTIGATION_STATUS_INVESTIGATED = "INVESTIGATION_STATUS_INVESTIGATED",
  INVESTIGATION_STATUS_COMPLETED = "INVESTIGATION_STATUS_COMPLETED",
}

export const INVESTIGATION_STATUS_TO_NAME_MAP: Record<InvestigationStatus, string> = {
  [InvestigationStatus.INVESTIGATION_STATUS_UNSPECIFIED]: "Unspecified",
  [InvestigationStatus.INVESTIGATION_STATUS_NOT_STARTED]: "Not Started",
  [InvestigationStatus.INVESTIGATION_STATUS_IN_PROGRESS]: "In Progress",
  [InvestigationStatus.INVESTIGATION_STATUS_INVESTIGATED]: "Investigated",
  [InvestigationStatus.INVESTIGATION_STATUS_COMPLETED]: "Completed",
};

export enum InvestigationStatusReason {
  INVESTIGATION_STATUS_REASON_UNSPECIFIED = "INVESTIGATION_STATUS_REASON_UNSPECIFIED",
  INVESTIGATION_STATUS_REASON_FILTERED_OUT = "INVESTIGATION_STATUS_REASON_FILTERED_OUT",
  INVESTIGATION_STATUS_REASON_QUOTA_EXCEEDED = "INVESTIGATION_STATUS_REASON_QUOTA_EXCEEDED",
}

export enum IncidentSessionType {
  INCIDENT_SESSION_TYPE_UNSPECIFIED = "INCIDENT_SESSION_TYPE_UNSPECIFIED",
  INCIDENT_SESSION_TYPE_AUTOMATED = "INCIDENT_SESSION_TYPE_AUTOMATED",
  INCIDENT_SESSION_TYPE_MANUAL = "INCIDENT_SESSION_TYPE_MANUAL",
}

export const INCIDENT_STATUS_NAME_MAP: Record<IncidentStatus, string> = {
  [IncidentStatus.INCIDENT_STATUS_UNSPECIFIED]: "",
  [IncidentStatus.INCIDENT_STATUS_INVESTIGATED]: "Investigated",
  [IncidentStatus.INCIDENT_STATUS_OPEN]: "Open",
  [IncidentStatus.INCIDENT_STATUS_RESOLVED]: "Resolved",
  [IncidentStatus.INCIDENT_STATUS_UNINVESTIGATED]: "Uninvestigated",
};

export enum SessionType {
  SESSION_TYPE_UNSPECIFIED = "SESSION_TYPE_UNSPECIFIED",
  SESSION_TYPE_USER = "SESSION_TYPE_USER",
  SESSION_TYPE_INCIDENT = "SESSION_TYPE_INCIDENT",
}
export enum PromptType {
  PROMPT_TYPE_UNSPECIFIED = "PROMPT_TYPE_UNSPECIFIED",
  PROMPT_TYPE_USER = "PROMPT_TYPE_USER",
  PROMPT_TYPE_INCIDENT = "PROMPT_TYPE_INCIDENT",
}

export enum SessionStatus {
  SESSION_STATUS_UNSPECIFIED = "SESSION_STATUS_UNSPECIFIED",
  SESSION_STATUS_PUBLIC = "SESSION_STATUS_PUBLIC",
  SESSION_STATUS_PRIVATE = "SESSION_STATUS_PRIVATE",
  SESSION_STATUS_ARCHIVED = "SESSION_STATUS_ARCHIVED",
}

// Create enum with PROMPT_STATUS_UNSPECIFIED, PROMPT_STATUS_COMPLETED, PROMPT_STATUS_FAILED, PROMPT_STATUS_CANCELLED, PROMPT_STATUS_IN_PROGRESS

export enum IncidentPromptStatus {
  PROMPT_STATUS_UNSPECIFIED = "PROMPT_STATUS_UNSPECIFIED",
  PROMPT_STATUS_COMPLETED = "PROMPT_STATUS_COMPLETED",
  PROMPT_STATUS_FAILED = "PROMPT_STATUS_FAILED",
  PROMPT_STATUS_CANCELLED = "PROMPT_STATUS_CANCELLED",
  PROMPT_STATUS_IN_PROGRESS = "PROMPT_STATUS_IN_PROGRESS",
}

export enum InviteUserStatus {
  INVITE_STATUS_UNSPECIFIED = "INVITE_STATUS_UNSPECIFIED",
  INVITE_STATUS_PENDING = "INVITE_STATUS_PENDING",
  INVITE_STATUS_COMPLETED = "INVITE_STATUS_COMPLETED",
  INVITE_STATUS_ERROR = "INVITE_STATUS_ERROR",
}

export enum SYSTEM_PROJECT_TYPE {
  UNSPECIFIED = "SYSTEM_PROJECT_TYPE_UNSPECIFIED",
  USAGE = "SYSTEM_PROJECT_TYPE_USAGE ",
}

export enum RESPONDER_THRESHOLD {
  UNSPECIFIED = "RESPONDER_THRESHOLD_UNSPECIFIED",
  ALL = "RESPONDER_THRESHOLD_ALL",
  MEDIUM = "RESPONDER_THRESHOLD_MEDIUM",
  HIGH = "RESPONDER_THRESHOLD_HIGH",
}

export const RESPONDER_PRIORITY_LIST: {
  label: string;
  value: RESPONDER_THRESHOLD;
}[] = [
  {
    label: "All",
    value: RESPONDER_THRESHOLD.ALL,
  },
  {
    label: "Medium",
    value: RESPONDER_THRESHOLD.MEDIUM,
  },
  {
    label: "High",
    value: RESPONDER_THRESHOLD.HIGH,
  },
];

export enum ConfigCatFeatureFlag {
  CHANGE_API_URL = "changeUrlPage",
  AUTO_GENERATE_AWS_EXTERNAL_ID = "awsexternalid",
  ENABLE_AWS_OIDC_FORM = "awsodic",
  ENABLE_AWS_ACCESS_KEY_FORM = "awsAccessKeyForm",
  ENABLED_CONNECTIONS = "enabledConnections",
  ENABLE_SESSION_FILE_UPLOAD = "enableSessionFileUpload",
  ENABLE_DOCS_BOT = "enableDocsBot",
  ALLOWED_EXTENSIONS_FOR_SESSION_CONTEXT = "allowedExtensionsForSessionContext",
  ENABLE_MSA = "enableMSA",
  SHOW_TELEMETRY_IN_SESSIONS = "showTelemetryInSessions",
  ENABLE_AUTH0 = "enableAuth0",
  ENABLE_UPDATE_PAGERDUTY_SERVICE = "enableUpdatePagerdutyService",
  ENABLE_SOURCE_TYPE_SELECTION = "enableSourceTypeSelection",
  IS_FILE_ATTACHMENT_MANDATORY = "isFileAttachmentMandatory",
  ENABLE_UPDATE_SERVICES = "enableUpdateServices",
  ENABLE_SESSION_SUMMARY_POLLING = "enableSessionSummaryPolling",
  HIDE_SESSION_STARTER_QUESTIONS = "hideSessionStarterQuestions",
  ENABLE_SPLUNK_TELEPORT = "enableSplunkTeleport",
  ENABLE_PROMETHEUS_TELEPORT = "enablePrometheusTeleport",
  ENABLE_PROJECT_SUMMARY = "enableProjectSummary",
  OVERRIDE_FAVICON = "overrideFavicon",
  OVERRIDE_LOGO = "overrideLogo",
  ENABLE_INSTRUCTIONS_BETA = "enableInstructionsBeta",
  ENABLE_ADVANCED_INSTRUCTIONS = "enableAdvancedInstructions",
  SLACK_SIGNUP_LINK = "slackSignupLink",
  ENABLE_PUBLIC_LINK_SHARING = "enablepubliclinksharing",
  whitelabeling = "whitelabeling",
  HIDE_GROUPING_INSTRUCTIONS = "hideGroupingInstructions",
  ENABLE_SPLIT_VISUALIZE_GRAPH = "enableSplitVisualizeGraph",
}

export const possibleMomentDateFormats = [
  "YYYY-MM-DD HH:mm:ss", // e.g. "2024-09-17 18:56:00"
  "MMMM D, YYYY, [at] h:mm A", // e.g. "September 17, 2024, at 6:56 PM"
  "MMM D, YYYY, h:mm A", // e.g. "Sep 17, 2024, 6:56 PM"
  "YYYY-MM-DD HH:mm:ss.SSSSSSZZ", // ISO-like with microseconds & offset
  "YYYY-MM-DD HH:mm:ss.SSSZZ", // With milliseconds & offset
  "MM/DD/YYYY h:mm:ss A", // e.g. "09/17/2024 6:56:00 PM" (US style)
  "DD-MM-YYYY h:mm:ss A", // e.g. "17-09-2024 6:56:00 PM" (European style)
  "DD-MM-YY", // e.g. "17-09-24"
  "YYYY-MM-DD", // ISO date (e.g. "2024-09-17")
  "DD/MM/YYYY", // e.g. "17/09/2024" (European style)
  "DD/MM/YY", // e.g. "17/09/24" (European short)
  "MM/DD/YYYY", // e.g. "09/17/2024" (US style)
  "MM/DD/YY", // e.g. "09/17/24" (US short)
  "YYYY-MM-DDTHH:mm:ss.SSSZZ", // With milliseconds and offset (ISO T separator)
  "YYYY-MM-DDTHH:mm:ss.SSS", // With milliseconds (no offset)
  "YYYY-MM-DDTHH:mm:ssZZ", // With seconds and offset
  "YYYY-MM-DDTHH:mm:ss", // With seconds only
  "YYYY-MM-DDTHH:mmZZ", // With minutes and offset
  "YYYY-MM-DDTHH:mm", // With minutes only
  "YYYY-MM-DDTHH", // With hours only
  "YYYY-MM-DDTHHZ", // With hours and offset
  "YYYY-MM-DDTHH:mm:ss.SSSSSS", // With microseconds (no offset)
  "dddd, D MMMM, YYYY h:mm:ss A", // Verbose format with day name (e.g. "Thursday, 17 September, 2024 6:56:00 PM")
  "YYYYMMDD HHmmss.SSSSSS", // Compact format with microseconds
  "MMMM D, YYYY", // e.g. "September 17, 2024"
  "MMM D, YYYY", // e.g. "Sep 17, 2024"
  "YYYYMMDD", // Compact format (e.g. "20240917")
  //Unix timestamp formats
  "X", // e.g. "1694976960" (seconds since epoch)
  "x", // e.g. "1694976960000" (milliseconds since epoch)
];

export enum InstructionType {
  UNSPECIFIED = "INSTRUCTION_TYPE_UNSPECIFIED",
  FILTER = "INSTRUCTION_TYPE_FILTER",
  SYSTEM = "INSTRUCTION_TYPE_SYSTEM",
  RCA = "INSTRUCTION_TYPE_RCA",
  GROUPING = "INSTRUCTION_TYPE_GROUPING",
}

export enum InstructionStatus {
  UNSPECIFIED = "INSTRUCTION_STATUS_UNSPECIFIED",
  ENABLED = "INSTRUCTION_STATUS_ENABLED",
  DISABLED = "INSTRUCTION_STATUS_DISABLED",
}

export enum FileType {
  PDF = "pdf",
  DOC = "doc",
  DOCX = "docx",
  TXT = "txt",
  CSV = "csv",
  XLS = "xls",
  XLSX = "xlsx",
  PPT = "ppt",
  PPTX = "pptx",
  ALL = "all",
}

export const CLIENT_IDENTIFIER = {
  WEB_APP: "web-app",
} as const;

export enum FILTER_STATUS {
  FILTER_STATUS_UNSPECIFIED = "FILTER_STATUS_UNSPECIFIED",
  FILTER_STATUS_FILTERED_OUT = "FILTER_STATUS_FILTERED_OUT",
  FILTER_STATUS_FILTERED_IN = "FILTER_STATUS_FILTERED_IN",
}
